import { FC } from 'react'

import { CaSubdivisionsMultiselect } from '@/components/inputs/CaSubdivisionsMultiselect'
import { CdnSelect } from '@/components/inputs/CdnSelect'
import { CountryAutocomplete } from '@/components/inputs/CountryAutocomplete'
import { OperatorAutocompleteMultiselect } from '@/components/inputs/OperatorAutocompleteMultiselect'
import { PeriodSelect } from '@/components/inputs/PeriodSelect/PeriodSelect'
import { PropertyTournamentSelect } from '@/components/inputs/PropertyTournamentSelect'
import { SportPropertySelect } from '@/components/inputs/SportPropertySelect'
import { UserAgentAutocomplete } from '@/components/inputs/UserAgentAutocomplete'
import { UsSubdivisionsMultiselect } from '@/components/inputs/UsSubdivisionsMultiselect'
import { DEVICE_PLATFORM_KIND } from '@/constants/devicePlatforms'
import { CANADA_COUNTRY, USA_COUNTRY } from '@/constants/regions/countries'
import { Filters } from '@/stores/historicalStatistics'

type ContentValue = NonNullable<Partial<Filters['content']>>

type HistoricalStatisticsFiltersFormProps = {
  filters: Filters
  onChange: (filters: Partial<Filters>) => void
}

export const HistoricalStatisticsFiltersForm: FC<HistoricalStatisticsFiltersFormProps> = ({
  filters,
  onChange
}) => {
  const {
    content,
    period,
    country,
    operators,
    usSubdivision,
    caSubdivision,
    userAgent,
    cdn,
    platform
  } = filters

  const onContentChange = (newValue: ContentValue) => {
    const { sport: newSport, property: newProperty, tournament: newTournament } = newValue
    const {
      sport: filtersSport,
      property: filtersProperty,
      tournament: filtersTournament
    } = content || {}

    onChange({
      content: {
        sport: newSport === null ? null : newSport || filtersSport || null,
        property: newProperty === null ? null : newProperty || filtersProperty || null,
        tournament: newTournament === null ? null : newTournament || filtersTournament || null
      }
    })
  }

  return (
    <>
      <PeriodSelect
        period={period}
        onChange={(period) => {
          onChange({
            period
          })
        }}
      />
      <CountryAutocomplete
        withFlags
        value={country}
        onChange={(country) => {
          onChange({
            country,
            usSubdivision: [],
            caSubdivision: []
          })
        }}
      />
      <UsSubdivisionsMultiselect
        value={usSubdivision}
        onChange={(usSubdivision) => {
          onChange({
            country: [USA_COUNTRY],
            usSubdivision,
            caSubdivision: []
          })
        }}
      />
      <CaSubdivisionsMultiselect
        value={caSubdivision}
        onChange={(caSubdivision) => {
          onChange({
            country: [CANADA_COUNTRY],
            usSubdivision: [],
            caSubdivision
          })
        }}
      />
      <OperatorAutocompleteMultiselect
        withLogos
        value={operators}
        onChange={(operators) => {
          onChange({
            operators
          })
        }}
      />
      <SportPropertySelect
        value={{ sport: content?.sport, property: content?.property }}
        onChange={{
          sport: (sport) => {
            onContentChange({ sport })
          },
          property: (property) => {
            onContentChange({ property })
          }
        }}
        onClear={{
          sport: () => {
            onContentChange({
              sport: null,
              property: null,
              tournament: null
            })
          },
          property: () => {
            onContentChange({
              property: null,
              tournament: null
            })
          }
        }}
      />
      <PropertyTournamentSelect
        tournament={content?.tournament || null}
        onChange={(tournament) => {
          onContentChange({ tournament })
        }}
        onClear={() => onContentChange({ tournament: null })}
        property={content?.property || null}
        disabled={!content?.property}
      />
      <UserAgentAutocomplete
        value={userAgent || platform || null}
        onChange={(platform) => {
          if (platform === null) {
            onChange({
              platform: null,
              userAgent: null
            })
            return
          }

          if (platform.kind === DEVICE_PLATFORM_KIND.DEVICES) {
            onChange({
              platform,
              userAgent: null
            })
            return
          }

          onChange({
            platform: null,
            userAgent: platform
          })
        }}
      />
      <CdnSelect
        value={cdn}
        onChange={(cdn) => {
          onChange({
            cdn
          })
        }}
      />
    </>
  )
}
